import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Seo } from "../components/Seo";
import { Crown, Check, Sparkles } from "lucide-react";
import api, { apiError } from "../lib/apiClient";
import { Editable } from "../components/Editable";
import { useAuth } from "../context/AuthContext";

// Every benefit is labelled by its real state. Nothing here is sold until partner collections exist and billing is tested.
const PERKS = [
  { text: "Every retailer purchase link on partner collections", status: "Coming with partner feeds" },
  { text: "Full family matching for all children", status: "Coming with partner feeds" },
  { text: "Whole-family pricing on every set", status: "Coming with partner feeds" },
  { text: "Early alerts when a matching collection appears", status: "Coming with partner feeds" },
  { text: "Official size charts and saved searches", status: "Free for everyone today" },
];
const SELLING_OPEN = false;

export default function Upgrade() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [plan, setPlan] = useState("monthly");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => {}); }, []);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  // Trial/comp users should still be able to subscribe — only truly PAID members see "Already Premium".
  const premium = user && user.plan_status === "premium";

  return (
    <div className="max-w-3xl mx-auto space-y-8 fade-up">
      <Seo title="SIBLINGO Premium (coming with partner collections)" description="SIBLINGO Premium will add whole-family matching, whole-family pricing and every retailer link once partner collections arrive. Sizing assistance and saved searches are free." noindex />
      <div className="text-center">
        <div className="w-16 h-16 rounded-3xl bg-gold/15 flex items-center justify-center mx-auto mb-4"><Crown className="text-gold-text" size={30} /></div>
        <Editable as="h1" id="upgrade.title" className="font-heading font-black text-4xl text-ink">SIBLINGO Premium</Editable>
        <Editable as="p" id="upgrade.subtitle" className="text-ink-soft mt-2 max-w-md mx-auto">Browsing, sizing assistance and saved searches are free. Premium will add whole-family shopping tools once our first partner collections arrive.</Editable>
        {!SELLING_OPEN && <p className="mt-3 text-sm text-ink-soft rounded-2xl border border-line bg-white p-3 max-w-md mx-auto" data-testid="upgrade-not-open">Premium isn't on sale yet — there is nothing to pay for until partner collections launch. Planned pricing is shown below.</p>}
      </div>

      {premium && (
        <div data-testid="already-premium" className="bg-gold/10 border border-gold/25 rounded-3xl p-5 text-center font-bold text-gold-text">You're a Premium member — enjoy all the perks!</div>
      )}

      {pricing && (
        <div className="grid sm:grid-cols-2 gap-4" role="radiogroup" aria-label="Choose a billing plan">
          <PlanCard active={plan === "monthly"} onClick={() => setPlan("monthly")} title="Monthly" price={pricing.monthly_price} unit="/mo" testid="plan-monthly" />
          <PlanCard active={plan === "yearly"} onClick={() => setPlan("yearly")} title="Yearly" price={pricing.yearly_price} unit="/yr" badge={`Save ${Math.round((1 - pricing.yearly_price / (pricing.monthly_price * 12)) * 100)}%`} testid="plan-yearly" />
        </div>
      )}

      <div className="bg-white rounded-3xl border border-line shadow-card p-6">
        <h3 className="font-heading font-extrabold text-lg text-ink mb-4 flex items-center gap-2"><Sparkles className="text-brand" size={20} /> <Editable id="upgrade.included-title">What's included</Editable></h3>
        <div className="grid sm:grid-cols-2 gap-2">
          {PERKS.map((p, i) => <p key={p.text} className="flex items-start gap-2 text-ink-soft py-1" data-testid={`upgrade-perk-${i}`}><Check size={17} className={`shrink-0 mt-0.5 ${p.status.startsWith("Free") ? "text-success" : "text-gold-text"}`} /> <span><Editable id={`upgrade.perk.${i}`}>{p.text}</Editable> <span className="block text-[11px] font-bold uppercase tracking-wide text-ink-muted">{p.status}</span></span></p>)}
        </div>
      </div>

      <button
        onClick={checkout}
        disabled={loading || premium || !SELLING_OPEN}
        data-testid="checkout-btn"
        className="w-full h-16 rounded-full bg-brand text-white font-bold text-lg hover:bg-brand-hover active:scale-95 transition-all shadow-md disabled:opacity-50"
      >
        {premium ? "Already Premium" : !SELLING_OPEN ? "Premium opens with partner collections" : loading ? "Redirecting to checkout…" : `Upgrade — $${pricing ? (plan === "monthly" ? pricing.monthly_price : pricing.yearly_price) : ""}${plan === "monthly" ? "/mo" : "/yr"}`}
      </button>
      <Editable as="p" id="upgrade.checkout-note" className="text-center text-xs text-ink-muted">Planned pricing. When Premium opens: secure checkout by Stripe · cancel anytime · SIBLINGO only charges membership, never your purchases. Existing memberships are unaffected.</Editable>
    </div>
  );
}

function PlanCard({ active, onClick, title, price, unit, badge, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} role="radio" aria-checked={active} aria-label={`${title} plan $${price}${unit}`} className={`relative text-left rounded-3xl border-2 p-6 transition-all outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 ${active ? "border-brand bg-brand-light shadow-card" : "border-line bg-white hover:border-brand/30"}`}>
      {badge && <span className="absolute top-4 right-4 bg-gold text-white text-xs font-bold px-2.5 py-1 rounded-full">{badge}</span>}
      <p className="font-bold text-ink-soft">{title}</p>
      <p className="font-heading font-black text-4xl text-ink mt-1">${price}<span className="text-base font-bold text-ink-muted">{unit}</span></p>
      <div className={`mt-4 w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? "border-brand bg-brand" : "border-line"}`}>{active && <Check size={12} className="text-white" />}</div>
    </button>
  );
}
